import AdminHeader from 'components/header';
import DateTimePicker from 'components/inputs/DateTimePicker';
import LayoutContainer from 'components/layout/LayoutContainer';
import { t } from 'i18next';
import React, { useCallback, useEffect, useState } from 'react';
import { Col, Row, Spinner } from 'react-bootstrap';
import Card from 'components/inputs/Card';
// import { SkeletonTheme } from 'react-loading-skeleton';
import { FETCH_UPTIME_REPORT, DOWNLOAD_UPTIME_REPORT } from 'actions/uptimeReport';
import { useDispatch, useSelector } from 'react-redux';
import ReactPagination from 'components/general/ReactPagination';
import moment from 'moment';
import { toast } from 'react-toastify';
import { BsDownload } from 'react-icons/bs';
import fileDownload from 'js-file-download';

const UptimeReport = () => {
  const [tableDateRange, setTableDateRange] = useState({
    start: moment().subtract(7, 'days').toDate(),
    end: moment().subtract(1, 'days').toDate(),
  });
  
  const dispatch = useDispatch();
  const uptimeData = useSelector((state) => state.uptimeReportReducer.uptimeReportData);
  const isLoading = useSelector((state) => state.downloadingLoader.isLoading);
  const today = moment().startOf('day');
  const defaultFrom = moment(today).subtract(7, 'days');
  const defaultTo = moment(today).subtract(1, 'days');
  const [validRange, setValidRange] = useState({
    start: defaultFrom.toDate(),
    end: defaultTo.toDate(),
  });
  const [startDate, setStartDate] = useState(defaultFrom.toDate());
  const [endDate, setEndDate] = useState(defaultTo.toDate());

  const getUptimeReport = useCallback(
    (data = {}) => {
      dispatch({ type: FETCH_UPTIME_REPORT.REQUEST, payload: data });
    },
    [dispatch]
  );

  // On mount, fetch for default range and page 1
  useEffect(() => {
    getUptimeReport({
      from: moment(validRange.start).format('YYYY-MM-DD'),
      to: moment(validRange.end).format('YYYY-MM-DD'),
      page: 1, // Always fetch page 1 on initial load
    });
  }, []); // Empty dependency array - only run once on mount

  const handleApply = () => {
    const start = moment(startDate);
    const end = moment(endDate);
    const diffInMonths = end.diff(start, 'months', true);

    // Always update validRange with the selected dates (for Excel download)
    setValidRange({ start: startDate, end: endDate });

    if (diffInMonths > 1) {
      toast.error("Date range is more than a month. Detailed data is available in the Excel report.");
      // Don't update tableDateRange or call getUptimeReport - keep showing last valid data
      return;
    }
    
    // Update table display range and fetch data only when date range is valid
    setTableDateRange({ start: startDate, end: endDate });
    getUptimeReport({
      from: start.format('YYYY-MM-DD'),
      to: end.format('YYYY-MM-DD'),
      page: 1,
    });
  };

  // Handle page change
  const handlePageClick = useCallback((pageVal) => {
    let page = 1;
    if (typeof pageVal === 'object' && pageVal !== null && pageVal.selected !== undefined) {
      page = pageVal.selected + 1;
    } else if (!isNaN(pageVal)) {
      page = Number(pageVal);
    }
  
    getUptimeReport({
      from: moment(validRange.start).format('YYYY-MM-DD'),
      to: moment(validRange.end).format('YYYY-MM-DD'),
      page,
    });
  }, [validRange.start, validRange.end, getUptimeReport]);

  // Download Excel function
  const downloadUptimeReport = useCallback(() => {
    const start = moment(validRange.start);
    const end = moment(validRange.end);

    const data = {
      from: start.format('YYYY-MM-DD'),
      to: end.format('YYYY-MM-DD'),
    };

    dispatch({
      type: DOWNLOAD_UPTIME_REPORT.REQUEST,
      payload: data,
      cb: (res) => {
        if (res) {
          if (res.status === 200) {
            fileDownload(res.data, `Uptime Report.xlsx`);
          } else if (res.status === 404) {
            toast.error('Error downloading report. Please try again.');
          }
        }
      },
    });
  }, [validRange.start, validRange.end, dispatch]);

  const downloadFile = () => {
    downloadUptimeReport();
  };

  const getDateArray = (start, end) => {
    const arr = [];
    let dt = moment(start);
    while (dt.isSameOrBefore(end, 'day')) {
      arr.push(dt.clone());
      dt.add(1, 'day');
    }
    return arr;
  };
  const dateArray = getDateArray(tableDateRange.start, tableDateRange.end);
  return (
    <LayoutContainer>
      <AdminHeader title={t('header.uptimeReport')} />
      {/* <SkeletonTheme baseColor="#1b2a38" highlightColor="#15212b" enableAnimation="true"> */}
        <div className="page-content-wrapper scrollable">
          <div className="data-report--main">
            <Card>
              <Row className="data-report__row align-items-center">
                <Col xl={6} lg={6} md={6}>
                  <div className="data-report-date_picker">
                    <DateTimePicker
                      idStart="start-date"
                      idEnd="end-date"
                      onChangeOfStartDate={(item) => setStartDate(item)}
                      onChangeOfEndDate={(item) => setEndDate(item)}
                      initialValueOfStartDate={startDate}
                      initialValueOfEndDate={endDate}
                      isValidDate={(current) => current.isBefore(today)}
                    />
                  </div>
                </Col>
                <Col xl={6} lg={6} md={6}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px', justifyContent: 'flex-start' }}>
                    <button className="btn btn-danger" onClick={handleApply}>
                    Search
                    </button>
                    {isLoading ? (
                      <div className="loader--block" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Spinner as="span" animation="border" size="sm" role="status" />
                        <span style={{ fontSize: '14px', color: '#6c757d' }}>Downloading...</span>
                      </div>
                    ) : (
                      <div className="report-excel-icon" onClick={downloadFile}>
                        <BsDownload title="Download" size={28} color={'#3c7cdd'} />
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
              <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                <table className="uptime-table">
                  <thead>
                    <tr>
                      <th>{t('Chargers')}</th>
                      {dateArray.map((date) => (
                        <th key={date.format('YYYY-MM-DD')}>{date.format('DD MMM')}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {uptimeData?.result?.map((charger, index) => (
                      <tr key={index}>
                        <td>{charger?.charger_id}</td>
                        {dateArray.map((date) => {
                          const daily = charger.dailyUptime?.find((d) => d.date === date.format('YYYY-MM-DD'));
                          const uptime = daily?.uptimePercent !== 'N/A' && daily?.uptimePercent !== undefined ? `${daily.uptimePercent}%` : '-';
                          return <td key={date.format('YYYY-MM-DD')}>{uptime}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {uptimeData && (
                <ReactPagination
                  currentPage={uptimeData?.page ?? 1}
                  limit={uptimeData?.limit ?? 10}
                  total={uptimeData?.totalResults}
                  handlePageClick={(pageVal) => handlePageClick(pageVal)}
                  totalPages={uptimeData?.totalPages ?? 1}
                  marginPagesDisplayed={1}
                />
              )}
            </Card>
          </div>
        </div>
      {/* </SkeletonTheme> */}
    </LayoutContainer>
  );
};

export default UptimeReport;